import { ALL_DIALER_WIDGET_IDS, type DialerWidgetId } from './dialer-widgets-layout';

export type DialerMessagesWidgetMode = 'classic' | 'two_columns' | 'three_columns';
export type DialerBelowMessagesContainerId = 'below_left' | 'below_middle' | 'below_right';

export type DialerBelowMessagesLayout = Record<DialerBelowMessagesContainerId, DialerWidgetId[]>;

const BELOW_MESSAGES_CONTAINER_ORDER: DialerBelowMessagesContainerId[] = [
  'below_left',
  'below_middle',
  'below_right',
];

export const DEFAULT_BELOW_MESSAGES_LAYOUT: DialerBelowMessagesLayout = {
  below_left: ['voice_transcript', 'action_items'],
  below_middle: ['ai_summary', 'conversation_highlights'],
  below_right: ['rag_suggestions'],
};

function isWidgetId(value: string): value is DialerWidgetId {
  return ALL_DIALER_WIDGET_IDS.includes(value as DialerWidgetId);
}

function arrayMove<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  const copy = [...items];
  const [item] = copy.splice(fromIndex, 1);
  copy.splice(toIndex, 0, item);
  return copy;
}

function emptyBelowMessagesLayout(): DialerBelowMessagesLayout {
  return {
    below_left: [],
    below_middle: [],
    below_right: [],
  };
}

export function getBelowMessagesColumnIds(
  mode: Exclude<DialerMessagesWidgetMode, 'classic'>,
): DialerBelowMessagesContainerId[] {
  if (mode === 'three_columns') return BELOW_MESSAGES_CONTAINER_ORDER;
  return BELOW_MESSAGES_CONTAINER_ORDER.slice(0, 2);
}

export function normalizeBelowMessagesLayout(
  raw: Partial<Record<DialerBelowMessagesContainerId, string[]>> | null | undefined,
): DialerBelowMessagesLayout {
  const next = emptyBelowMessagesLayout();
  const seen = new Set<DialerWidgetId>();

  for (const containerId of BELOW_MESSAGES_CONTAINER_ORDER) {
    const values = raw?.[containerId] ?? [];
    for (const value of values) {
      if (!isWidgetId(value) || seen.has(value)) continue;
      seen.add(value);
      next[containerId].push(value);
    }
  }

  for (const widgetId of ALL_DIALER_WIDGET_IDS) {
    if (seen.has(widgetId)) continue;
    next.below_right.push(widgetId);
  }

  return next;
}

function flattenLayout(layout: DialerBelowMessagesLayout): DialerWidgetId[] {
  return BELOW_MESSAGES_CONTAINER_ORDER.flatMap((containerId) => layout[containerId]);
}

export function rebalanceBelowMessagesLayout(
  layout: DialerBelowMessagesLayout,
  mode: Exclude<DialerMessagesWidgetMode, 'classic'>,
): DialerBelowMessagesLayout {
  const normalized = normalizeBelowMessagesLayout(layout);
  const next = emptyBelowMessagesLayout();
  const columns = getBelowMessagesColumnIds(mode);
  const orderedWidgets = flattenLayout(normalized);
  const chunkSize = Math.ceil(orderedWidgets.length / columns.length);

  columns.forEach((columnId, columnIndex) => {
    const start = columnIndex * chunkSize;
    const end = start + chunkSize;
    next[columnId] = orderedWidgets.slice(start, end);
  });

  return next;
}

export function resolveBelowMessagesContainer(
  layout: DialerBelowMessagesLayout,
  value: string | null | undefined,
  mode: Exclude<DialerMessagesWidgetMode, 'classic'>,
): DialerBelowMessagesContainerId | null {
  if (!value) return null;
  const columns = getBelowMessagesColumnIds(mode);
  if (columns.includes(value as DialerBelowMessagesContainerId)) {
    return value as DialerBelowMessagesContainerId;
  }
  if (!isWidgetId(value)) return null;

  for (const columnId of columns) {
    if (layout[columnId].includes(value)) return columnId;
  }

  return null;
}

export function moveBelowMessagesWidgetInLayout(
  layout: DialerBelowMessagesLayout,
  activeWidgetId: DialerWidgetId,
  overId: string | null | undefined,
  mode: Exclude<DialerMessagesWidgetMode, 'classic'>,
): DialerBelowMessagesLayout {
  const sourceContainer = resolveBelowMessagesContainer(layout, activeWidgetId, mode);
  const targetContainer = resolveBelowMessagesContainer(layout, overId, mode);

  if (!sourceContainer || !targetContainer) return layout;
  if (activeWidgetId === overId) return layout;

  if (sourceContainer === targetContainer) {
    const items = layout[sourceContainer];
    const oldIndex = items.indexOf(activeWidgetId);
    if (oldIndex < 0) return layout;

    const nextIndex =
      typeof overId === 'string' && isWidgetId(overId) ? items.indexOf(overId) : items.length - 1;
    if (nextIndex < 0 || nextIndex === oldIndex) return layout;

    return {
      ...layout,
      [sourceContainer]: arrayMove(items, oldIndex, nextIndex),
    };
  }

  const sourceItems = [...layout[sourceContainer]];
  const targetItems = [...layout[targetContainer]];
  const sourceIndex = sourceItems.indexOf(activeWidgetId);
  if (sourceIndex < 0) return layout;

  sourceItems.splice(sourceIndex, 1);

  const targetIndex =
    typeof overId === 'string' && isWidgetId(overId)
      ? Math.max(0, targetItems.indexOf(overId))
      : targetItems.length;
  targetItems.splice(targetIndex, 0, activeWidgetId);

  return {
    ...layout,
    [sourceContainer]: sourceItems,
    [targetContainer]: targetItems,
  };
}
